import { env } from "@canucci/base/env";
import { normalizeCanvasView } from "@canucci/base/canvas";
import { AuthenticatePermissions, AuthResult } from "@canucci/base/auth/types";
import { initDiscoveryClient } from "@/api/apiClient";
import { getCanvasView as getCanvasViewRequest } from "@/api/openapi/discovery/sdk.gen";
import {
  type CanvasData,
  // We might need HTTPValidationError later if we parse specific errors
} from "./apitypes";

/** Coerce wire payloads so optional Canvas fields have stable defaults. */
export const normalizeCanvasData = normalizeCanvasView<CanvasData>;

export class ApiError extends Error {
  status: number;
  constructor(
    message: string,
    status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Define types based on the OpenAPI schema
// ... existing code ...
// --- API Client ---

const API_BASE_URL = env.VITE_API_URL as string;
const ORGANIZATION_API_URL = env.VITE_ORGANIZATION_API_URL as string;
const normalizedOrganizationApiUrl = ORGANIZATION_API_URL.endsWith("/")
  ? ORGANIZATION_API_URL.slice(0, -1)
  : ORGANIZATION_API_URL;

/**
 * Handles API responses, checking for errors and parsing JSON.
 * @param response - The fetch Response object.
 * @returns The parsed JSON data.
 * @throws An error if the response is not ok or JSON parsing fails.
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401 || response.status === 403) {
    const body: unknown = await response.json().catch(() => undefined);
    throw new ApiError(
      `API request failed with status ${response.status}: ${response.statusText}`,
      response.status,
      body,
    );
  }

  if (!response.ok) {
    // let errorData; // Consider importing HTTPValidationError here if needed
    try {
      /* errorData = */ await response.json(); // Try to parse error data, but don't use it yet
    } catch (_e) {
      // Use _e to indicate the variable is intentionally unused
      // Ignore JSON parsing error if response body is empty or not JSON
    }
    // You might want more specific error handling based on status code or errorData
    throw new ApiError(`API request failed with status ${response.status}: ${response.statusText}`, response.status);
  }
  // Handle cases where the response might be empty (e.g., 204 No Content)
  if (response.status === 204) {
    return undefined as T; // Or handle as appropriate for your application
  }
  return response.json() as Promise<T>;
}

export function toApiError(error: unknown, fallbackMessage: string): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  const status =
    typeof (error as { status?: number })?.status === "number"
      ? (error as { status: number }).status
      : typeof (error as { statusCode?: number })?.statusCode === "number"
        ? (error as { statusCode: number }).statusCode
        : typeof (error as { response?: { status?: number } })?.response?.status === "number"
          ? (error as { response: { status: number } }).response.status
          : typeof (error as { response?: { statusCode?: number } })?.response?.statusCode === "number"
            ? (error as { response: { statusCode: number } }).response.statusCode
            : error instanceof Response
              ? error.status
              : typeof (error as { cause?: { status?: number } })?.cause?.status === "number"
                ? (error as { cause: { status: number } }).cause.status
                : typeof (error as { cause?: { statusCode?: number } })?.cause?.statusCode === "number"
                  ? (error as { cause: { statusCode: number } }).cause.statusCode
                  : typeof (
                        error as {
                          cause?: { response?: { status?: number } };
                        }
                      )?.cause?.response?.status === "number"
                    ? (error as { cause: { response: { status: number } } }).cause.response.status
                    : undefined;
  const message =
    (error as { message?: string })?.message ||
    (error as { response?: { statusText?: string } })?.response?.statusText ||
    fallbackMessage;
  return new ApiError(message, typeof status === "number" ? status : 500, error);
}

export function unwrapClientData<T>(result: unknown): T {
  if (result && typeof result === "object" && "data" in result) {
    return (result as { data: T }).data;
  }

  return result as T;
}

/**
 * Performs a health check on the API.
 * @returns A promise that resolves with the health check response (currently an empty object).
 */
export async function healthCheck(): Promise<Record<string, never>> {
  const response = await fetch(`${API_BASE_URL}/health`);
  return handleResponse<Record<string, never>>(response);
}

/**
 * Fetches canvas data for a given external ID and optional language.
 * @param externalId - The external ID of the canvas.
 * @param language - The optional language code (e.g., 'en', 'sv').
 *                   If undefined, the API should return the canvas's default language version.
 * @returns A promise that resolves with the Canvas data.
 */
export async function getCanvas(
  externalId: string,
  language?: string,
  _isProtected = false,
  token?: string,
): Promise<CanvasData> {
  const client = initDiscoveryClient();
  const query = language ? { language } : undefined;

  try {
    const response = await getCanvasViewRequest({
      client,
      throwOnError: false,
      path: { external_id: externalId },
      query,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (response.error) {
      const detail = (() => {
        if (!("detail" in response.error)) {
          return "Request failed.";
        }
        const errorDetail = response.error.detail;
        if (typeof errorDetail === "string") {
          return errorDetail;
        }
        if (Array.isArray(errorDetail)) {
          return errorDetail
            .map((item) => {
              if (typeof item === "string") {
                return item;
              }
              if (item && typeof item === "object") {
                if ("msg" in item && typeof item.msg === "string") {
                  return item.msg;
                }
                if ("message" in item && typeof item.message === "string") {
                  return item.message;
                }
              }
              return "Request failed.";
            })
            .join("; ");
        }
        return "Request failed.";
      })();
      throw new ApiError(detail, response.response?.status ?? 500, response.error);
    }
    return normalizeCanvasData(unwrapClientData<CanvasData>(response));
  } catch (error) {
    throw toApiError(error, "Failed to fetch canvas data.");
  }
}

/**
 * Authenticates the user with the backend.
 * @param token - The Firebase ID token.
 * @returns A promise that resolves with the authentication result.
 */
export async function authenticate(token: string): Promise<AuthResult> {
  const response = await fetch(`${normalizedOrganizationApiUrl}/user/authenticate/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token }),
  });

  const permissions = await handleResponse<AuthenticatePermissions>(response);
  return { permissions };
}
